import { LocalDate } from '@js-joda/core';

import { AppointmentPrescriptionOnly } from '@dreryk/egabinet-appointment-frontend-api';
import { HealthcareProviderLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { ServiceLocationId, ServiceLocationType } from '@dreryk/egabinet-healthcare-provider-published-language-api';
import { PatientSimple } from '@dreryk/egabinet-patient-frontend-api';
import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';
import {
   ReceptionServiceWithPractitionersSimple,
   ServicesTemplate,
   ServicesTemplateQuery,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { performCqrsQuerySearch } from '@dreryk/command-query-react-common';

import { PRESCRIPTION_ONLY_TEMPLATE_TAG } from '../../prescriptionVisitTemplates/PrescriptionVisitTemplates.constants';

import { PrescriptionVisitFormBean } from './PrescriptionVisitForm.types';

const getSingleOrNull = <T>(items: T[]): T | null => (items.length === 1 ? items[0] : null);

export const addFormInitializer = (
   healthcareProviderLocations: HealthcareProviderLocation[],
   practitioners: DoctorSimple[]
) => (): PrescriptionVisitFormBean => ({
   healthcareProviderLocation: getSingleOrNull(healthcareProviderLocations),
   practitioner: getSingleOrNull(practitioners),
   patient: null,
   date: LocalDate.now(),
   note: '',
   receptionService: null,
});

export const restoreFormInitializer = (
   appointment: AppointmentPrescriptionOnly,
   patient: PatientSimple,
   healthcareProviderLocations: HealthcareProviderLocation[],
   practitioners: DoctorSimple[],
   receptionServices: ReceptionServiceWithPractitionersSimple[]
) => (): PrescriptionVisitFormBean => {
   const healthcareProviderLocation =
      healthcareProviderLocations.find(
         (location) =>
            ServiceLocationId.of(
               location.locationIdentifier.type as ServiceLocationType,
               location.locationIdentifier.locationId
            ).asString() === appointment.locationId.asString()
      ) ?? null;

   const practitioner =
      practitioners.find((doctor) => doctor.practitionerId === appointment.practitionerId.asNumber()) ?? null;

   const restoredReceptionServiceId = appointment.receptionServiceIds?.[0]?.asNumber();
   const receptionService =
      receptionServices.find((service) => service.id.asNumber() === restoredReceptionServiceId) ?? null;

   return {
      date: appointment.date,
      healthcareProviderLocation: healthcareProviderLocation,
      practitioner: practitioner,
      patient: patient,
      note: appointment.note ?? '',
      receptionService: receptionService,
   };
};

export const resolveReceptionServiceFromTemplate = async (
   practitionerId: number,
   receptionServices: ReceptionServiceWithPractitionersSimple[]
): Promise<ReceptionServiceWithPractitionersSimple | null> => {
   const servicesTemplates = await performCqrsQuerySearch<ServicesTemplate>(() => {
      const query = new ServicesTemplateQuery();
      query.practitionerId = PractitionerId.of(practitionerId);
      query.templateTag = PRESCRIPTION_ONLY_TEMPLATE_TAG;
      return query;
   });

   const [servicesTemplate] = servicesTemplates;
   if (!servicesTemplate?.receptionServiceId) {
      return null;
   }

   return (
      receptionServices.find((service) => service.id.asNumber() === servicesTemplate.receptionServiceId!.asNumber()) ??
      null
   );
};

export const filterReceptionServicesByPractitioner = (
   receptionServices: ReceptionServiceWithPractitionersSimple[],
   practitioner: DoctorSimple | null
): ReceptionServiceWithPractitionersSimple[] => {
   if (!practitioner) {
      return receptionServices;
   }

   return receptionServices.filter((service) =>
      service.practitioners.some(
         ({ practitioner: servicePractitioner }) => servicePractitioner.id === practitioner.practitionerId
      )
   );
};

export const nameLabelExtractor = (item?: { name?: string }): string => item?.name ?? '';

export const doctorSimpleLabelExtractor = (item?: DoctorSimple | null): string =>
   item ? `${item.lastName} ${item.firstName}` : '';

export const prescriptionHealthcareProviderLocationViewVisitor = (
   healthcareProviderLocations: HealthcareProviderLocation[],
   selectedHealthcareProviderLocation: HealthcareProviderLocation | null,
   touched: boolean | undefined
) => <ACCEPT>(view: { text: (options: { editable: boolean }) => ACCEPT; select: () => ACCEPT }): ACCEPT => {
   if (!selectedHealthcareProviderLocation || touched) {
      return view.select();
   }

   return view.text({ editable: healthcareProviderLocations.length > 1 });
};

export const prescriptionPractitionerViewVisitor = (
   practitioners: DoctorSimple[],
   selectedPractitioner: DoctorSimple | null,
   touched: boolean | undefined
) => <ACCEPT>(view: { text: () => ACCEPT; select: () => ACCEPT }): ACCEPT => {
   if (!selectedPractitioner || touched || practitioners.length > 1) {
      return view.select();
   }

   return view.text();
};
